import { GroupBase, StylesConfig } from "react-select";

export type JevisSelectOption = { value: string; label: string };

const BRAND = {
  primary: "#cd212a",
  secondary: "#2e436a",
};

export function jevisSelectStyles(
  size: "sm" | "md" = "md",
  tone: "default" | "onDark" = "default"
): StylesConfig<JevisSelectOption, boolean, GroupBase<JevisSelectOption>> {
  const minHeight = size === "sm" ? 40 : 44;
  const fontSize = size === "sm" ? "0.8125rem" : "0.875rem";
  const onDark = tone === "onDark";

  return {
    control: (base, state) => ({
      ...base,
      minHeight,
      borderRadius: "0.75rem",
      borderWidth: "1px",
      borderColor: onDark
        ? state.isFocused
          ? "rgba(255, 255, 255, 0.95)"
          : "rgba(255, 255, 255, 0.55)"
        : state.isFocused
          ? "rgba(205, 33, 42, 0.45)"
          : state.isDisabled
            ? "rgba(46, 67, 106, 0.1)"
            : "rgba(46, 67, 106, 0.18)",
      backgroundColor: onDark
        ? state.isDisabled
          ? "rgba(255, 255, 255, 0.75)"
          : "#ffffff"
        : state.isDisabled
          ? "rgba(46, 67, 106, 0.04)"
          : "rgba(46, 67, 106, 0.02)",
      boxShadow: onDark
        ? state.isFocused
          ? "0 0 0 3px rgba(255, 255, 255, 0.35)"
          : "0 4px 14px rgba(0, 0, 0, 0.12)"
        : state.isFocused
          ? "0 0 0 3px rgba(205, 33, 42, 0.14)"
          : "none",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      transition: "border-color 0.15s ease, box-shadow 0.15s ease",
      "&:hover": {
        borderColor: onDark
          ? "rgba(255, 255, 255, 0.9)"
          : state.isDisabled
            ? "rgba(46, 67, 106, 0.1)"
            : state.isFocused
              ? "rgba(205, 33, 42, 0.45)"
              : "rgba(205, 33, 42, 0.3)",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      padding: "2px 10px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: BRAND.secondary,
    }),
    placeholder: (base) => ({
      ...base,
      color: "rgba(46, 67, 106, 0.45)",
      fontSize,
      fontWeight: 500,
    }),
    singleValue: (base) => ({
      ...base,
      color: BRAND.secondary,
      fontSize,
      fontWeight: 600,
    }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: onDark
        ? state.isFocused
          ? BRAND.primary
          : BRAND.secondary
        : state.isFocused
          ? BRAND.primary
          : "rgba(46, 67, 106, 0.5)",
      padding: "0 10px",
      cursor: "pointer",
      transition: "color 0.15s ease, transform 0.15s ease",
      transform: state.selectProps.menuIsOpen ? "rotate(180deg)" : undefined,
      "&:hover": { color: BRAND.primary },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "rgba(46, 67, 106, 0.4)",
      padding: "0 6px",
      cursor: "pointer",
      "&:hover": { color: BRAND.primary },
    }),
    multiValue: (base) => ({
      ...base,
      borderRadius: "0.5rem",
    }),
    multiValueLabel: (base) => ({
      ...base,
      cursor: "default",
    }),
    multiValueRemove: (base) => ({
      ...base,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "rgba(205, 33, 42, 0.15)",
        color: BRAND.primary,
      },
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.75rem",
      overflow: "hidden",
      border: "1px solid rgba(46, 67, 106, 0.12)",
      boxShadow: "0 12px 40px rgba(46, 67, 106, 0.18)",
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: "6px",
      maxHeight: 280,
    }),
    option: (base, state) => ({
      ...base,
      fontSize,
      borderRadius: "0.5rem",
      padding: "10px 12px",
      cursor: state.isDisabled ? "not-allowed" : "pointer",
      backgroundColor: state.isSelected
        ? BRAND.secondary
        : state.isFocused
          ? "rgba(46, 67, 106, 0.08)"
          : "transparent",
      color: state.isSelected ? "#fff" : BRAND.secondary,
      fontWeight: state.isSelected ? 600 : 500,
      "&:active": {
        backgroundColor: state.isSelected ? BRAND.secondary : "rgba(205, 33, 42, 0.12)",
      },
    }),
    noOptionsMessage: (base) => ({
      ...base,
      fontSize,
      color: "rgba(46, 67, 106, 0.5)",
    }),
    loadingMessage: (base) => ({
      ...base,
      fontSize,
      color: "rgba(46, 67, 106, 0.5)",
    }),
  };
}

/** @deprecated Usa AppSelect o jevisSelectStyles — mantenuto per compatibilità */
export const panelSelectStyles = jevisSelectStyles("md");
